/**
 * icons — small inline-SVG line icons for the Design editor controls.
 *
 * DESIGN.md is a navy line-icon system; emoji-as-UI-icon renders differently per
 * OS/browser and reads as cutesy/AI-slop, so the editor's load-bearing controls
 * (visibility, lock, delete, undo/redo, save state, add) use these 1.5px-stroke
 * navy glyphs instead. Each is presentational (aria-hidden via the consumer's
 * srOnly text label), so state stays icon+text, never color-alone.
 */
import React from 'react';

// Stroke inherits the surrounding text color (currentColor) so each icon adopts
// its control's color — navy line-icons in the panel, white on the green save
// button, red in the error banner — keeping state expressed by icon + text, not
// a single hardcoded hue.
const Svg = ({ children, size = 16, strokeWidth = 1.6 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

export const EyeIcon = props => (
    <Svg {...props}>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
    </Svg>
);

export const EyeOffIcon = props => (
    <Svg {...props}>
        <path d="M9.9 5.2A9.8 9.8 0 0 1 12 5c6.5 0 10 7 10 7a16.8 16.8 0 0 1-3.3 4.1" />
        <path d="M6.2 6.7A16.7 16.7 0 0 0 2 12s3.5 7 10 7a9.8 9.8 0 0 0 4-.8" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        <path d="M3 3l18 18" />
    </Svg>
);

export const LockIcon = props => (
    <Svg {...props}>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
);

export const UnlockIcon = props => (
    <Svg {...props}>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 7.5-2" />
    </Svg>
);

export const TrashIcon = props => (
    <Svg {...props}>
        <path d="M4 7h16" />
        <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
        <path d="M10 11v6M14 11v6" />
    </Svg>
);

export const UndoIcon = props => (
    <Svg {...props}>
        <path d="M9 7 4 12l5 5" />
        <path d="M4 12h11a5 5 0 0 1 0 10h-1" />
    </Svg>
);

export const RedoIcon = props => (
    <Svg {...props}>
        <path d="m15 7 5 5-5 5" />
        <path d="M20 12H9a5 5 0 0 0 0 10h1" />
    </Svg>
);

export const CheckIcon = props => (
    <Svg {...props}>
        <path d="M5 12.5 10 17 19 7" />
    </Svg>
);

export const WarningIcon = props => (
    <Svg {...props}>
        <path d="M12 3 2 20h20L12 3Z" />
        <path d="M12 10v4" />
        <path d="M12 17.5v.5" />
    </Svg>
);

export const PlusIcon = props => (
    <Svg {...props}>
        <path d="M12 5v14M5 12h14" />
    </Svg>
);

// ---- per-layer-type glyphs, so the layer list is scannable at a glance ----

export const ImageIcon = props => (
    <Svg {...props}>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="9" cy="10" r="1.6" />
        <path d="m4 18 5-5 4 4 3-3 4 4" />
    </Svg>
);

export const TextIcon = props => (
    <Svg {...props}>
        <path d="M5 6h14" />
        <path d="M12 6v13" />
        <path d="M9 19h6" />
    </Svg>
);

export const BadgeIcon = props => (
    <Svg {...props}>
        <rect x="3" y="8" width="18" height="8" rx="4" />
        <path d="M8 12h8" />
    </Svg>
);

export const ShapeIcon = props => (
    <Svg {...props}>
        <rect x="4" y="5" width="16" height="14" rx="2" />
    </Svg>
);

// ---- text alignment: the ragged edge IS the glyph, so the three read as one
// segmented control at a glance (long rule = the box, short rules = the text).

export const AlignLeftIcon = props => (
    <Svg {...props}>
        <path d="M4 6h16" />
        <path d="M4 12h10" />
        <path d="M4 18h13" />
    </Svg>
);

export const AlignCenterIcon = props => (
    <Svg {...props}>
        <path d="M4 6h16" />
        <path d="M7 12h10" />
        <path d="M6 18h12" />
    </Svg>
);

export const AlignRightIcon = props => (
    <Svg {...props}>
        <path d="M4 6h16" />
        <path d="M10 12h10" />
        <path d="M7 18h13" />
    </Svg>
);

export const CropIcon = props => (
    <Svg {...props}>
        <path d="M6 2v16a2 2 0 0 0 2 2h14" />
        <path d="M2 6h14a2 2 0 0 1 2 2v14" />
    </Svg>
);

export const InfoIcon = props => (
    <Svg {...props}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <path d="M12 8h.01" />
    </Svg>
);

// ---- text style toggles (the Inspector's "Stil" row) ----

export const BoldIcon = props => (
    <Svg {...props}>
        <path d="M7 4h7a4 4 0 0 1 0 8H7z" />
        <path d="M7 12h8a4 4 0 0 1 0 8H7z" />
    </Svg>
);

export const ItalicIcon = props => (
    <Svg {...props}>
        <path d="M19 4h-9" />
        <path d="M14 20H5" />
        <path d="M15 4 9 20" />
    </Svg>
);

export const UnderlineIcon = props => (
    <Svg {...props}>
        <path d="M6 3v7a6 6 0 0 0 12 0V3" />
        <path d="M4 21h16" />
    </Svg>
);

export const StrikethroughIcon = props => (
    <Svg {...props}>
        <path d="M16.3 5.6A5.2 5.2 0 0 0 12 4c-2.6 0-4.3 1.4-4.3 3.3 0 1 .4 1.8 1.5 2.5" />
        <path d="M8 18.2A5.6 5.6 0 0 0 12 20c2.7 0 4.4-1.4 4.4-3.4 0-.9-.3-1.6-1-2.2" />
        <path d="M4 12h16" />
    </Svg>
);

export const UppercaseIcon = props => (
    <Svg {...props}>
        <path d="m3 18 4.2-12L11.4 18" />
        <path d="M4.6 14h5.2" />
        <path d="m12.6 18 4.2-12L21 18" />
        <path d="M14.2 14h5.2" />
    </Svg>
);

export const TextShadowIcon = props => (
    <Svg {...props}>
        <path d="M4 5h10" />
        <path d="M9 5v12" />
        <path d="M13 10h7" opacity="0.45" />
        <path d="M16.5 10v10" opacity="0.45" />
    </Svg>
);

// ---- small chrome glyphs (close, chevrons, ellipsis) — the drawn versions of
// what used to be ✕ ▲ ▼ … text characters, which render differently per OS ----

export const CloseIcon = props => (
    <Svg {...props}>
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
    </Svg>
);

export const ChevronUpIcon = props => (
    <Svg {...props}>
        <path d="m5 15 7-7 7 7" />
    </Svg>
);

export const ChevronDownIcon = props => (
    <Svg {...props}>
        <path d="m5 9 7 7 7-7" />
    </Svg>
);

export const EllipsisIcon = props => (
    <Svg {...props}>
        <path d="M5 12h.01" />
        <path d="M12 12h.01" />
        <path d="M19 12h.01" />
    </Svg>
);
